import { createFileRoute, Link } from '@tanstack/react-router';
import { Boxes, Palette, Type, Sparkles, Grid3x3, MousePointer2, FileInput, Table } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export const Route = createFileRoute('/_authenticated/_layout/components/')({
  component: ComponentsPage,
  staticData: {
    title: 'Components',
    icon: Boxes,
  },
});

function ComponentsPage() {
  const sections = [
    {
      title: 'Brand Colors',
      description: 'Primary brand colors, gradients, semantic colors, and neutral palette',
      icon: Palette,
      path: '/components/colors',
      color: 'bg-purple-50 text-purple-600',
    },
    {
      title: 'Typography',
      description: 'Font family, hierarchy, weights, colors, and typography in practice',
      icon: Type,
      path: '/components/typography',
      color: 'bg-blue-50 text-blue-600',
    },
    {
      title: 'Iconography',
      description: 'Icon library, sizes, stroke widths, colors, and usage patterns',
      icon: Sparkles,
      path: '/components/iconography',
      color: 'bg-amber-50 text-amber-600',
    },
    {
      title: 'Tables & Components',
      description: 'Cards, tables, dialogs, overlays, tabs, separators, alerts, and progress indicators',
      icon: Table,
      path: '/components/tables',
      color: 'bg-green-50 text-green-600',
    },
    {
      title: 'Wizards',
      description: 'Multi-step selection cards with gradients and animations',
      icon: Grid3x3,
      path: '/components/wizards',
      color: 'bg-pink-50 text-pink-600',
    },
    {
      title: 'Buttons',
      description: 'Primary actions, semantic colors, icon buttons, and Google button',
      icon: MousePointer2,
      path: '/components/buttons',
      color: 'bg-indigo-50 text-indigo-600',
    },
    {
      title: 'Forms',
      description: 'Text inputs, selects, pickers, checkboxes, radio buttons, and switches',
      icon: FileInput,
      path: '/components/forms',
      color: 'bg-teal-50 text-teal-600',
    },
    {
      title: 'Animations',
      description: 'Loading, vibrate, wiggle, shimmer animations, transitions, and badges',
      icon: Sparkles,
      path: '/components/animations',
      color: 'bg-orange-50 text-orange-600',
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between py-4 h-[72px]">
        <div className="flex items-center gap-3">
          <Boxes className="h-7 w-7 text-gray-900 flex-shrink-0" />
          <h1 className="text-2xl font-semibold text-gray-900">Design System</h1>
        </div>
      </div>

      <div className="flex-1 pb-8">
        <p className="text-gray-900 mb-6">
          Explore GlitchAds design system components, patterns, and guidelines. Each section showcases the building blocks of our user interface with implementation examples and best practices.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sections.map((section) => (
            <Link
              key={section.path}
              to={section.path}
              className="block group"
            >
              <Card className="rounded-lg border bg-white hover:shadow-md transition-shadow cursor-pointer h-full">
                <CardHeader className="p-6 pb-4">
                  <div className={`w-12 h-12 rounded-lg ${section.color} flex items-center justify-center mb-4`}>
                    <section.icon className="h-6 w-6" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-gray-900 group-hover:text-[#290BD9] transition-colors">
                    {section.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 pt-0">
                  <p className="text-gray-900">
                    {section.description}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
